/**
 * Dual-layer persistence: API-backed (Cloudflare Workers KV) + localStorage fallback.
 * Works offline-first with localStorage, syncs to API when available.
 * Auth token is injected via setAuthToken() for authenticated API calls.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

type TasksPayload = Record<string, boolean>;

interface ApiTaskState {
  weekKey: string;
  tasks: TasksPayload;
  updatedAt: string | null;
}

let authToken: string | null = null;

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

async function apiGet(weekKey: string): Promise<TasksPayload | null> {
  try {
    const res = await fetch(`${API_BASE}/api/tasks/${weekKey}`, { headers: getHeaders() });
    if (!res.ok) return null;
    const data: ApiTaskState = await res.json();
    return data.tasks && Object.keys(data.tasks).length > 0 ? data.tasks : null;
  } catch {
    return null;
  }
}

async function apiPut(weekKey: string, tasks: TasksPayload): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/tasks/${weekKey}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ tasks }),
    });
  } catch {
    // Silently fail — localStorage is the source of truth
  }
}

async function apiDelete(weekKey: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/tasks/${weekKey}`, { method: 'DELETE', headers: getHeaders() });
  } catch {
    // Silently fail
  }
}

// --- localStorage helpers ---

function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* Storage full or unavailable */ }
}

function lsRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* Ignore */ }
}

// --- Cloud sync for all user data ---

export interface UserDataBundle {
  calendarHistory?: Record<string, unknown>;
  customTasks?: unknown[];
  customSchedule?: unknown[] | null;
  settings?: Record<string, unknown>;
  updatedAt?: string;
}

async function cloudLoadAll(): Promise<UserDataBundle | null> {
  if (!authToken) return null;
  try {
    const res = await fetch(`${API_BASE}/api/user/data`, { headers: getHeaders() });
    if (!res.ok) return null;
    const json = await res.json() as { data: UserDataBundle | null };
    return json.data;
  } catch {
    return null;
  }
}

async function cloudSaveAll(bundle: UserDataBundle): Promise<void> {
  if (!authToken) return;
  try {
    await fetch(`${API_BASE}/api/user/data`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(bundle),
    });
  } catch {
    // Silently fail
  }
}

// --- Public persistence API ---

export interface PersistedState {
  weekKey: string;
  tasks: Record<string, boolean>;
}

const STORAGE_KEY = 'cleaning-schedule-state';

export const persistence = {
  setAuthToken(token: string | null): void {
    authToken = token;
  },

  async load(weekKey: string): Promise<Record<string, boolean>> {
    // Try localStorage first (fast)
    const stored = lsGet(STORAGE_KEY);
    let localTasks: Record<string, boolean> = {};

    if (stored) {
      try {
        const state: PersistedState = JSON.parse(stored);
        if (state.weekKey === weekKey) {
          localTasks = state.tasks;
        } else {
          lsRemove(STORAGE_KEY);
        }
      } catch {
        lsRemove(STORAGE_KEY);
      }
    }

    // Try API (may have newer data from another device)
    const apiTasks = await apiGet(weekKey);
    if (apiTasks) {
      const merged = { ...localTasks, ...apiTasks };
      lsSet(STORAGE_KEY, JSON.stringify({ weekKey, tasks: merged }));
      return merged;
    }

    return localTasks;
  },

  async save(weekKey: string, tasks: Record<string, boolean>): Promise<void> {
    lsSet(STORAGE_KEY, JSON.stringify({ weekKey, tasks }));
    await apiPut(weekKey, tasks);
  },

  async reset(weekKey: string): Promise<void> {
    lsRemove(STORAGE_KEY);
    await apiDelete(weekKey);
  },

  getLocal(key: string): string | null {
    return lsGet(key);
  },

  setLocal(key: string, value: string): void {
    lsSet(key, value);
  },

  removeLocal(key: string): void {
    lsRemove(key);
  },

  // Cloud sync methods
  async loadAllFromCloud(): Promise<UserDataBundle | null> {
    return cloudLoadAll();
  },

  async syncAllToCloud(bundle: UserDataBundle): Promise<void> {
    return cloudSaveAll(bundle);
  },
};
